import React, { Fragment, useState, useCallback, useEffect } from "react"
import {
  Typography,
  Row,
  Col,
  Space,
  Button,
  Form,
  Skeleton,
  Checkbox,
  Card,
  Radio,
  Tooltip,
  Select,
  Modal,
  Input,
} from "antd"
import { useDispatch, useSelector } from "react-redux"
import { submitRADSettings, getRADSettings, getRadPlans, changePlan, changeAddonSuspendStatus, changeDefaultAddress, getAddonAddressSettings } from "../../Actions/RAD"
import { changeAddonStatus } from "../../Actions/EnitureStore"

const { Title } = Typography
const { Option } = Select
const initialState = {
  always_quote_residential_delivery: false,
  return_rates: false,
  residential_delivery_auto_detect: false,
  unconfirmed_address_type: 1,
  suppress_rad_notation: 1,
  always_residential_pickup_delivery: false,
  address_validation_enabled: false,
  address_validation_alert_content: 1,
  address_validation_custom_message: '',
  address_validation_presentation: 1,
}

function ShippingGroupsComponent() {
  const [settings, setSettings] = useState(initialState)
  const dispatch = useDispatch()
  const { token, radSettings, installedCarriers, installedAddons, radPlans, store, addonSettings } = useSelector(state => state)
  const [pickup, setPickup] = useState(true)
  const [suspend, setSuspend] = useState(false)
  const [cancelSubsriptionVisible, SetCancelSubsriptionVisible] = useState(false)
  const [newPlan, SetNewPlan] = useState(0)
  const [isCharging, setIsCharging] = useState(false)
  const [address, setAddress] = useState(1)
  const [isAddonDisabled, setIsAddonDisabled] = useState(false)
  const [customMessageError, setCustomMessageError] = useState(false)
  const RAD_ADDON = 'RAD'

  useEffect(() => {
    if (!radSettings) {
      dispatch(getRADSettings(token))
    }

    if (!radPlans) {
      dispatch(getRadPlans(token))
    }

    if (installedCarriers) {
      for (const ic of installedCarriers) {
        if (ic.slug === "ltl-quotes" && ic.is_enabled) {
          setPickup(false)
        }
      }
    }

    if (radSettings) {
      if (radSettings?.settings) {
        const newSettings = JSON.parse(radSettings?.settings) ?? {}
        setSettings(prevSettings => ({
          ...prevSettings,
          ...newSettings,
        }))
      }
    }

    if (addonSettings) {
      setAddress(addonSettings.unconfirmed_default)
    }
  }, [dispatch, radSettings, token, radPlans, addonSettings])

  const isRadSuspend = radPlans?.currentPackage?.status === null || radPlans?.currentPackage?.status === 3 || radPlans?.currentPackage?.status === 0

  const isAddonPaused = suspend || radPlans?.currentPackage?.status === 3

  // !isRadSuspend applies to both branches now, not just the first - a suspended plan has
  // status === 3, which alone satisfied the second branch's "status !== 0" check, making this
  // true even while suspended.
  const isRadInstalled = ((installedAddons?.find(add => add.short_code === RAD_ADDON && add.is_enabled == 1)) ||
    (radPlans?.currentPackage !== null && radPlans?.currentPackage?.status !== 0)) && !isRadSuspend && !isAddonDisabled
    ? true
    : false

  const handleStateChange = useCallback(e => {
    const { name, checked } = e.target

    setSettings(prevSettings => ({
      ...prevSettings,
      [name]: checked,
    }))
  }, [])

  const onFinish = useCallback(() => {
    const requiresCustomMessage = !isAddonPaused && isAddressValidationAllowed && settings.address_validation_enabled && settings.address_validation_alert_content === 2

    if (requiresCustomMessage && !settings.address_validation_custom_message?.trim()) {
      setCustomMessageError(true)
      return
    }
    setCustomMessageError(false)

    // Set residential_delivery_auto_detect as opposite of always_quote_residential_delivery
    settings.residential_delivery_auto_detect = !settings.always_quote_residential_delivery

    dispatch(
      submitRADSettings(
        {
          ...radSettings,
          settings,
        },
        token
      )
    )
  }, [dispatch, radSettings, settings, token, isAddonPaused])

  const handleChangePlan = () => {
    if (isCharging) return
    setIsCharging(true)
    // No residential_settings row exists yet, so this is the first time RAD
    // is being activated - the backend won't enable auto-detect until a
    // settings row is saved, so fire that save automatically here instead of
    // waiting for the user to separately press "Save".
    const isFirstTimeActivation = !radSettings?.settings
    dispatch(changePlan(token, newPlan?.id, (val) => {
      SetCancelSubsriptionVisible(val)
      setIsCharging(false)
    }, () => {
      if (isFirstTimeActivation) {
        dispatch(
          submitRADSettings(
            {
              ...radSettings,
              settings: {
                ...settings,
                residential_delivery_auto_detect: !settings.always_quote_residential_delivery,
              },
            },
            token
          )
        )
      }
    }))
  }

  const chanePlanAction = (plan_value) => {
    if (plan_value === 'disable' || plan_value === 7 || plan_value === 22) {
      // Refresh radSettings on success - on a fresh store, activating a plan
      // here (e.g. the free trial) causes the backend to create the default
      // residential_settings row, but nothing else re-fetches it into Redux,
      // leaving the Fedex LTL "residential only" checkbox stuck disabled.
      dispatch(changePlan(token, plan_value, SetCancelSubsriptionVisible, () => {
        dispatch(getRADSettings(token))
      }))
    } else {
      SetNewPlan(
        radPlans?.allRadPackages.find(({ id }) => id === plan_value)
      )
      SetCancelSubsriptionVisible(true)
    }
  }

  const handleAddonSuspendStatus = (value) => {
    let action = value ? 3 : 1
    dispatch(
      changeAddonSuspendStatus(
        radPlans?.currentPackage?.package_id,
        token,
        action
      )
    )
  }

  const onChange = (e) => {
    setAddress(e.target.value)
    dispatch(changeDefaultAddress('addon_id', token, e.target.value))
  }

  // Get the dropdown value based on backend data
  const getDropdownValue = () => {
    const cp = radPlans?.currentPackage
    if (!cp || cp === null) return 'disable'
    if (cp.status === 0) return 'disable'

    const status = cp.package_to_be_charge_status
    if (status === 0 || status === '0' || status === null || status === undefined) return 'disable'

    // For all cases where auto-renew is enabled, return the plan ID
    if (status === 1 || status === '1' || status === 'Trial' || status === 'Development Plan') {
      return cp.to_be_charge_package_id ? cp.to_be_charge_package_id : 'disable'
    }

    // If status is a numeric ID, return it
    if (typeof status === 'number') return status
    if (typeof status === 'string' && /^\d+$/.test(status)) return parseInt(status)

    return 'disable'
  }

  if (!radSettings) return <Skeleton active />

  const cp = radPlans?.currentPackage
  const isPlanExpired = !!cp?.expiry_time && new Date() > new Date(cp.expiry_time)
  const noAutoRenewal = getDropdownValue() === 'disable'
  const remainingBudget = parseFloat(cp?.remaining_budget || 0)
  const isBudgetZero = !cp?.is_unlimited && remainingBudget === 0
  const isBudgetTooLow = !cp?.is_unlimited && remainingBudget > 0 && remainingBudget < 0.5

  const radStatusLabel = !cp
    ? null
    : cp.status === 3
      ? 'Suspended'
      : noAutoRenewal && isBudgetZero
        ? 'Expired (Budget Fully Consumed)'
        : noAutoRenewal && isBudgetTooLow
          ? 'Your remaining budget is too low to process another lookup'
          : isPlanExpired
            ? 'Expired'
            : 'Active'

  // Checkout address alerts run entirely on RAD lookups, so "Enable" must only be
  // selectable while the status above reads "Active" - not suspended, expired, or
  // budget-exhausted with no pending renewal. isRadInstalled alone isn't enough:
  // its installedAddons branch treats the RAD app being installed as sufficient
  // even when the subscription itself is disabled/suspended/expired.
  const isRadActive = radStatusLabel === 'Active'

  // "Always quote residential delivery" skips address-type detection entirely (every
  // address is quoted as residential, no lookup happens), so there's no detection result
  // left for Checkout address alerts to evaluate - keep it mutually exclusive with that
  // checkbox, same as the "Default unconfirmed address types" section above.
  const isAddressValidationAllowed = isRadInstalled && isRadActive && !settings.always_quote_residential_delivery

  return (
    <Fragment>
      <Row gutter={24} justify='center'>
        <Col
          className='gutter-row mb-3'
          xs={24}
          sm={24}
          md={24}
          lg={24}
          xl={22}>
          <Title level={3}>
            Ship-to Address Settings
          </Title>
        </Col>
      </Row>

      <Row gutter={24} justify='center' className={'mb-3'}>
        <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={22}>
          <h3 style={{ fontWeight: 'bold' }}>Features included with plan</h3>
          <Card style={{ width: '100%' }}>
            <Row gutter={30}>
              <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={24}>
                <Form.Item className="mb-0">
                  <Checkbox
                    name="return_rates"
                    checked={settings.return_rates}
                    onChange={e => handleStateChange(e)}
                  >
                    Do not return rate if the shipping address appears to be a
                    post office box
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={24}>
                <Form.Item className={"mb-0"}>
                  <Checkbox
                    name="always_quote_residential_delivery"
                    checked={settings.always_quote_residential_delivery}
                    onChange={e => handleStateChange(e)}
                  >
                    Always quote residential delivery
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={24} justify='center' className={'mb-3'}>
        <Col className='gutter-row' xs={24} sm={24} md={24} lg={24} xl={22}>
          <h3 style={{ fontWeight: 'bold' }}>Features requiring an add-on subscription</h3>
          <Card style={{ width: '100%' }}>
            <Row gutter={30}>
              <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={24}>
                <Form.Item className="mb-2">
                  <label>
                    <strong>Auto-renew</strong>
                  </label>
                  <Select
                    value={getDropdownValue()}
                    style={{ width: '100%', marginBottom: '20px' }}
                    onChange={chanePlanAction}
                    name='plan_value'
                  >
                    <Option key='disable' value='disable'>
                      Disable (default)
                    </Option>

                    {/* If current selection is not in the allRadPackages list, add it from currentPackage */}
                    {(() => {
                      const currentValue = getDropdownValue()
                      const currentPackage = radPlans?.currentPackage

                      // Check if current value exists in allRadPackages
                      const existsInList = radPlans?.allRadPackages?.some(p => String(p.id) === String(currentValue))

                      // If not in list but we have currentPackage data, render it
                      if (!existsInList && currentValue !== 'disable' && currentPackage?.current_package_name) {
                        const packageName = currentPackage.current_package_name
                        const totalHits   = currentPackage.total_allowed_hits
                        let label = ''

                        if (packageName === 'Trial') {
                          label = `$5.00 (Free Trial)/15 days: US 5¢ ea. | CA 7¢ ea. | Others 10¢ ea.`
                        } else if (packageName === 'Development Plan') {
                          label = `${Intl.NumberFormat('en-US').format(totalHits)}/5 years ${packageName} ($0)`
                        } else {
                          label = currentPackage.currentPlanText || `$${currentPackage.current_package_cost}/month`
                        }

                        return (
                          <Option key={String(currentValue)} value={currentValue}>
                            {label}
                          </Option>
                        )
                      }
                      return null
                    })()}

                    {radPlans?.allRadPackages?.length > 0
                      ? radPlans?.allRadPackages?.map(plan => {
                        if (plan.cost !== 0) {
                          return (
                            <Option key={plan.id} value={plan.id} disabled={store?.plan_level === 'Sandbox Store'}>
                              {plan.plan_label}
                            </Option>
                          );
                        } else if (plan.name === 'Development Plan' && store?.plan_level === 'Sandbox Store') {
                          return (
                            <Option key={plan.id} value={plan.id}>
                              {Intl.NumberFormat('en-US').format(plan.htis)}/5 years {plan.name} (${plan.cost})
                            </Option>
                          );
                        } else if (plan.name === 'Trial' && store?.plan_level !== 'Sandbox Store') {
                          return (
                            <Option key={plan.id} value={plan.id} disabled={store?.plan_level === 'Sandbox Store'}>
                              $5.00 (Free Trial)/15 days: US 5¢ ea. | CA 7¢ ea. | Others 10¢ ea.
                            </Option>
                          );
                        }
                        return null;
                      })
                      : null
                    }
                  </Select>
                  <p style={{ marginTop: '-16px', marginBottom: '16px', fontSize: '12px', color: '#888' }}>
                    Others include: Australia, Belgium, Great Britain
                  </p>

                  {radPlans?.currentPackage === null ? (
                    ""
                  ) : (
                    <Fragment>
                      {radPlans?.currentPackage?.current_package_name === null ? (
                        <h1 className='mb-2'>
                          <b>No plan is activated.</b>
                        </h1>
                      ) : (
                        <Fragment>
                          <label>
                            <strong>Current plan</strong>
                          </label>

                          <div style={{ width: '100%', marginBottom: '20px' }}>
                            <p style={{ marginBottom: '0' }}>
                              <strong>Plan Budget:</strong>{' '}
                              {cp?.is_unlimited ? 'Unlimited' : `$${cp?.plan_budget}`}
                              {cp?.current_package_name === 'Trial' && ' (Free Trial)'}
                            </p>
                            {!cp?.is_unlimited && (
                              <p style={{ marginBottom: '0' }}>
                                <strong>Remaining Budget:</strong>{' '}
                                {`$${cp?.remaining_budget}`}
                              </p>
                            )}
                            <p style={{ marginBottom: '0' }}>
                              <strong>Status:</strong>{' '}
                              {radStatusLabel}
                            </p>
                            <p style={{ marginBottom: '0' }}>
                              <strong>Start date:</strong>{' '}
                              {new Date(cp?.subscription_time).toDateString().substring(4)}
                            </p>
                            <p style={{ marginBottom: '0' }}>
                              <strong>End date:</strong>{' '}
                              {new Date(cp?.expiry_time).toDateString().substring(4)}
                            </p>
                          </div>

                          <Checkbox
                            onChange={(e) => {
                              handleAddonSuspendStatus(e.target.checked);
                              setSuspend(e.target.checked);
                            }}
                            checked={suspend || radPlans?.currentPackage?.status === 3}
                            defaultValue={radPlans?.currentPackage?.status}
                          >
                            Suspend use (Temporarily suspend address verification and budget consumption.)
                          </Checkbox>
                        </Fragment>
                      )}
                    </Fragment>
                  )}
                </Form.Item>
              </Col>


              <Col className="gutter-row mt-3" xs={24} sm={24} md={24} lg={24} xl={24}>
                <div className="mb-1">
                  <h3 style={{ fontWeight: "bold" }}>Address type detection</h3>
                  <p style={{ marginBottom: '10px' }}>
                    When enabled, the app will automatically detect the address type for ship-to addresses in the USA, Canada, Australia, Belgium, and Great Britain and inform the shipping provider of the result. When a residential address is detected, the residential fee will be included in the carrier’s rate estimates. Address lookups are billed against your monthly budget at a rate that varies by country. Ship-to addresses in unsupported countries will not reduce the budget. The next subscription begins when the current one expires or is depleted, whichever comes first. Refer to the User Guide for more information.
                  </p>
                </div>
              </Col>

              {/* Auto-detect residential addresses checkbox hidden - always set to true */}
              <Col
                className="gutter-row mt-1"
                xs={24}
                sm={14}
                md={12}
                lg={12}
                xl={8}
              >
                <label
                  className="ml-5"
                  style={{
                    marginLeft: "",
                    color: (!suspend && radPlans?.currentPackage?.status !== 3 && isRadActive && !settings.always_quote_residential_delivery) ? '#262626' : 'rgba(0, 0, 0, 0.25)',
                  }}
                >
                  Default unconfirmed address types to:
                </label>
              </Col>
              <Col xs={24} sm={10} md={12} lg={12} xl={16}>
                <Radio.Group
                  className="mt-1 mb-2"
                  onChange={e =>
                    setSettings(prevSettings => ({
                      ...prevSettings,
                      unconfirmed_address_type: +e.target.value,
                    }))
                  }
                  value={settings.unconfirmed_address_type}
                >
                  <Space
                    direction="vertical"
                    style={{
                      marginLeft: "1rem",
                    }}
                  >
                    <Radio
                      disabled={suspend || radPlans?.currentPackage?.status === 3 || !isRadActive || settings.always_quote_residential_delivery}
                      value={1}
                    >
                      Residential
                    </Radio>
                    <Radio
                      disabled={suspend || radPlans?.currentPackage?.status === 3 || !isRadActive || settings.always_quote_residential_delivery}
                      value={2}
                    >
                      Commercial
                    </Radio>
                  </Space>
                </Radio.Group>
              </Col>
              <Col className="gutter-row" xs={14} sm={14} md={12} lg={12} xl={8}>
                <label
                  className="ml-5"
                  style={{
                    marginLeft: "",
                    color: (!suspend && radPlans?.currentPackage?.status !== 3 && isRadActive && !settings.always_quote_residential_delivery) ? '#262626' : 'rgba(0, 0, 0, 0.25)',
                  }}
                >
                  Address type disclosure:
                </label>
              </Col>
              <Col xs={24} sm={10} md={12} lg={12} xl={16}>
                <Radio.Group
                  className="mb-2"
                  onChange={e =>
                    setSettings(prevSettings => ({
                      ...prevSettings,
                      suppress_rad_notation: +e.target.value,
                    }))
                  }
                  value={settings?.suppress_rad_notation}
                >
                  <Space
                    direction="vertical"
                    style={{
                      marginLeft: "1rem",
                    }}
                  >
                    <Radio
                      disabled={suspend || radPlans?.currentPackage?.status === 3 || !isRadActive || settings.always_quote_residential_delivery}
                      value={1}
                    >
                      Inform the shopper when the ship-to address is identified as
                      residential address
                    </Radio>
                    <Radio
                      disabled={suspend || radPlans?.currentPackage?.status === 3 || !isRadActive || settings.always_quote_residential_delivery}
                      value={0}
                    >
                      Don't disclose the address type to the shopper
                    </Radio>
                  </Space>
                </Radio.Group>
              </Col>

              <Col className="gutter-row mt-3" xs={24} sm={24} md={24} lg={24} xl={24}>
                <div className="mb-1">
                  <h3 style={{ fontWeight: "bold" }}>Checkout address alerts</h3>
                  <p style={{ marginBottom: '10px' }}>
                    When enabled, the ship-to address will be evaluated by the system. If there is a concern with the address, an alert will appear above the shipping quotes informing the visitor.
                  </p>
                </div>
              </Col>

              <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={24}>
                <Form.Item className="mb-2">
                  <Checkbox
                    name="address_validation_enabled"
                    checked={settings.address_validation_enabled}
                    disabled={isAddonPaused || !isAddressValidationAllowed}
                    onChange={e => handleStateChange(e)}
                  >
                    Enable
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col
                className="gutter-row mt-1"
                xs={24}
                sm={14}
                md={12}
                lg={12}
                xl={8}
              >
                <label
                  className="ml-5"
                  style={{
                    marginLeft: "",
                    color: (!isAddonPaused && isAddressValidationAllowed && settings.address_validation_enabled) ? '#262626' : 'rgba(0, 0, 0, 0.25)',
                  }}
                >
                  Checkout alert content:
                </label>
              </Col>
              <Col xs={24} sm={10} md={12} lg={12} xl={16}>
                <Radio.Group
                  className="mt-1 mb-2"
                  disabled={isAddonPaused || !isAddressValidationAllowed || !settings.address_validation_enabled}
                  onChange={e => {
                    const value = +e.target.value
                    setSettings(prevSettings => ({
                      ...prevSettings,
                      address_validation_alert_content: value,
                    }))
                    if (value !== 2) {
                      setCustomMessageError(false)
                    }
                  }}
                  value={settings.address_validation_alert_content}
                >
                  <Space direction="vertical" style={{ marginLeft: "1rem" }}>
                    <Radio value={1}>
                      Display the explanation provided by the system.
                    </Radio>
                    <Radio value={2}>
                      Display the message provided in the input field below:
                    </Radio>
                  </Space>
                </Radio.Group>
                  <Form.Item
                    className="mb-0"
                    style={{ marginLeft: "1rem" }}
                    validateStatus={customMessageError ? 'error' : ''}
                    help={customMessageError ? 'This field is required.' : ''}
                  >
                    <Input
                      disabled={isAddonPaused || !isAddressValidationAllowed || !settings.address_validation_enabled || settings.address_validation_alert_content !== 2}
                      value={settings.address_validation_custom_message}
                      onChange={e => {
                        const { value } = e.target
                        setSettings(prevSettings => ({
                          ...prevSettings,
                          address_validation_custom_message: value,
                        }))
                        if (value.trim()) {
                          setCustomMessageError(false)
                        }
                      }}
                      required
                    />
                  </Form.Item>
              </Col>

              <Col
                className="gutter-row mt-1"
                xs={24}
                sm={14}
                md={12}
                lg={12}
                xl={8}
              >
                <label
                  className="ml-5"
                  style={{
                    marginLeft: "",
                    color: (!isAddonPaused && isAddressValidationAllowed && settings.address_validation_enabled) ? '#262626' : 'rgba(0, 0, 0, 0.25)',
                  }}
                >
                  Presentation of shipping rates:
                </label>
              </Col>
              <Col xs={24} sm={10} md={12} lg={12} xl={16}>
                <Radio.Group
                  className="mt-1 mb-2"
                  disabled={isAddonPaused || !isAddressValidationAllowed || !settings.address_validation_enabled}
                  onChange={e =>
                    setSettings(prevSettings => ({
                      ...prevSettings,
                      address_validation_presentation: +e.target.value,
                    }))
                  }
                  value={settings.address_validation_presentation}
                >
                  <Space direction="vertical" style={{ marginLeft: "1rem" }}>
                    <Radio value={1}>
                      Continue to display shipping quotes (if available) when there is a concern with the ship-to address.
                    </Radio>
                    <Radio value={2}>
                      Do not display shipping quotes when there is a concern with the ship-to address.
                    </Radio>
                  </Space>
                </Radio.Group>
              </Col>
            </Row>
            <Row gutter={30}>
              <Col className="gutter-row" xs={24} sm={24} md={24} lg={24} xl={24}>
                <Form.Item style={{ textAlign: "right", marginBottom: "0" }}>
                  <Space>
                    <Button
                      onClick={onFinish}
                      type="primary"
                      size="medium"
                      htmlType="submit"
                    >
                      Save
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Modal
        title='Note!'
        visible={cancelSubsriptionVisible}
        onCancel={() => { if (!isCharging) { SetCancelSubsriptionVisible(false) } }}
        centered
        onOk={handleChangePlan}
        okText='Confirm'
        okButtonProps={{ loading: isCharging, disabled: isCharging }}
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        Note! You have elected to enable the automatically detect residential addresses feature. By
        confirming this election you will be charged for the{' '}
        {newPlan?.plan_label || `$${newPlan?.cost}/month`} plan.
        To ensure service continuity the plan will automatically renew each month,
        or when the plan is depleted, whichever comes first. You can change which
        plan is put into effect on the next renewal date by updating the selection
        on this page at anytime.
      </Modal>
    </Fragment>
  )
}

export default ShippingGroupsComponent
